/**
 * Platform-neutral per-message feedback store (ELARA-738).
 *
 * The good/bad message-feedback flow was previously embedded in the web-only `MessageViewState`
 * alongside its DOM scroll machinery. The feedback half — the bad-response dialog target signals and
 * the `SubmitMessageFeedback` flow call + thank-you toast — has no DOM dependency, so it lives here
 * and is consumed identically by the web `MessageViewState` (which delegates to it) and the React
 * Native message list (ELARA-708), which has no scroll-container state to attach it to.
 *
 * The store owns only the dialog target + the flow/toast side effect; the rating currently shown on
 * a thumb is read from the message model (`msg.feedback?.rating`), which round-trips through
 * Firestore after the flow writes it — so there's no local per-message rating state to keep in sync.
 */
import type { AppFlowsContainer } from '#flows.js';

import type { ToastState } from './ToastState.js';

import { createState } from '#lib/signals-utils.js';

/** A message feedback rating. `good` submits immediately; `bad` opens the comment dialog. */
export type MessageFeedbackRating = 'good' | 'bad';

export function createMessageFeedbackState(flows: AppFlowsContainer, toastState: ToastState) {
  const state = createState({
    /** Whether the bad-response comment dialog is open. */
    feedbackDialogOpen: false,
    /** Chat id of the message the open dialog targets. */
    feedbackTargetChatId: undefined as string | undefined,
    /** Message id the open dialog targets. */
    feedbackTargetMessageId: undefined as string | undefined,

    /** Opens the bad-response dialog for a specific message. */
    openFeedbackDialogForMessage(chatId: string, messageId: string) {
      this.feedbackTargetChatId = chatId;
      this.feedbackTargetMessageId = messageId;
      this.feedbackDialogOpen = true;
    },

    /** Closes the dialog and clears its target. */
    closeFeedbackDialog() {
      this.feedbackDialogOpen = false;
      this.feedbackTargetChatId = undefined;
      this.feedbackTargetMessageId = undefined;
    },

    /**
     * Submits feedback for a message via the `SubmitMessageFeedback` flow and shows a thank-you toast.
     * Handles both ratings; closes the dialog after a `bad` submission (success or failure).
     */
    async submitFeedback(chatId: string, messageId: string, rating: MessageFeedbackRating, comment?: string) {
      try {
        await flows.submitMessageFeedback({
          chatId,
          messageId,
          rating,
          comment: comment?.trim() || undefined,
        });
        toastState.addToast({
          title: 'Thanks for your feedback',
          variant: 'success',
          description:
            rating === 'good'
              ? 'Your rating helps us learn what works well.'
              : 'Your response will help us improve future answers.',
        });
      } catch (error) {
        console.error('[MessageFeedbackState] Failed to submit message feedback', error);
        toastState.addToast({
          title: 'Could not save feedback',
          description: 'There was a problem saving your feedback. Please try again.',
          variant: 'destructive',
        });
      } finally {
        if (rating === 'bad') {
          this.closeFeedbackDialog();
        }
      }
    },

    /** Submits `bad` feedback for the message the dialog currently targets. */
    async submitFeedbackForDialog(comment?: string) {
      const chatId = this.feedbackTargetChatId;
      const messageId = this.feedbackTargetMessageId;
      if (!chatId || !messageId) {
        this.closeFeedbackDialog();
        return;
      }
      await this.submitFeedback(chatId, messageId, 'bad', comment);
    },
  });
  return state;
}

/** Platform-neutral per-message feedback store. All properties are signals. */
export type MessageFeedbackState = ReturnType<typeof createMessageFeedbackState>;
